import { type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';

import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

type ConfirmOptions = {
    title: string;
    message: string;
    body?: ReactNode;
    confirmLabel?: string;
    /** Displays the confirm button in red, for an action that loses data. */
    isDestructive?: boolean;
    /** null displays a single acknowledgement button. */
    cancelLabel?: string | null;
    signal?: AbortSignal;
};

function ConfirmDialog({
    title,
    message,
    body,
    confirmLabel = 'Oui',
    isDestructive = false,
    cancelLabel = 'Non',
    onResolve,
}: ConfirmOptions & { onResolve: (value: boolean) => void }) {
    return (
        <Dialog open onOpenChange={(open) => !open && onResolve(false)}>
            <DialogContent showCloseButton={false}>
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeader>
                <p className="whitespace-pre-line text-sm text-foreground">{message}</p>
                {body}
                <DialogFooter>
                    <Button variant={isDestructive ? 'destructive' : 'default'} onClick={() => onResolve(true)}>
                        {confirmLabel}
                    </Button>
                    {cancelLabel !== null && (
                        <Button variant="outline" onClick={() => onResolve(false)}>
                            {cancelLabel}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export const confirmPopup = ({ signal, ...options }: ConfirmOptions): Promise<boolean> => {
    if (signal?.aborted) return Promise.resolve(false);
    return new Promise<boolean>((resolve) => {
        const container = document.createElement('div');
        document.body.appendChild(container);
        const root = createRoot(container);
        let settled = false;
        const abort = () => handleResolve(false);

        function handleResolve(value: boolean): void {
            if (settled) return;
            settled = true;
            signal?.removeEventListener('abort', abort);
            // A route unmount can abort during a React effect cleanup.
            queueMicrotask(() => {
                root.unmount();
                container.remove();
                resolve(value);
            });
        }

        signal?.addEventListener('abort', abort, { once: true });
        root.render(<ConfirmDialog {...options} onResolve={handleResolve} />);
    });
};
